/**
 * Weapon Registry
 *
 * Centralized, data-driven registry of all weapons in the game.
 * Supports dynamic weapon registration for plugins/mods.
 *
 * Features:
 * - Register weapons from code or JSON data
 * - Query weapons by rarity, type, loot source
 * - Weighted random selection for loot tables
 * - Extensible via plugins
 */

import { Weapon, WeaponFireMode, ProjectileType } from './Weapon.js';
import { ItemRarity } from './Item.js';
import { getSharedLogger } from './utils/loggerProxy.js';

const weaponRegistryLogger = getSharedLogger('WEAPON');

// Kept as named imports so registry code can reference them; also re-exported
// for consumers that used to reach them through the registry module.
export { Weapon, WeaponFireMode, ProjectileType, ItemRarity };

  class WeaponRegistry {
    constructor() {
      this.weapons = new Map();
      this.defaultWeaponId = 'assault_rifle';
    }

    /**
     * Register a weapon
     * @param {Weapon|Object} weaponOrConfig - Weapon instance or config object
     * @returns {boolean} - Success
     */
    register(weaponOrConfig) {
      let weapon;
      if (weaponOrConfig instanceof Weapon) {
        weapon = weaponOrConfig;
      } else {
        // Create weapon from config
        weapon = new Weapon(weaponOrConfig);
      }

      if (this.weapons.has(weapon.id)) {
        weaponRegistryLogger.warn(`WeaponRegistry: Weapon '${weapon.id}' already registered, overwriting`);
      }

      this.weapons.set(weapon.id, weapon);
      return true;
    }

    /**
     * Register multiple weapons from JSON data
     * @param {Object[]} weaponConfigs - Array of weapon configurations
     */
    registerBulk(weaponConfigs) {
      for (const config of weaponConfigs) {
        this.register(config);
      }
    }

    /**
     * Unregister a weapon
     * @param {string} weaponId
     * @returns {boolean} - Success
     */
    unregister(weaponId) {
      return this.weapons.delete(weaponId);
    }

    /**
     * Get weapon by ID
     * @param {string} weaponId
     * @returns {Weapon|null}
     */
    get(weaponId) {
      return this.weapons.get(weaponId) || null;
    }

    /**
     * Check if weapon exists
     * @param {string} weaponId
     * @returns {boolean}
     */
    has(weaponId) {
      return this.weapons.has(weaponId);
    }

    /**
     * Get all weapon IDs
     * @returns {string[]}
     */
    getAllIds() {
      return Array.from(this.weapons.keys());
    }

    /**
     * Get all weapons
     * @returns {Weapon[]}
     */
    getAll() {
      return Array.from(this.weapons.values());
    }

    /**
     * Set the default weapon ID
     * @param {string} weaponId
     */
    setDefaultWeapon(weaponId) {
      if (!this.weapons.has(weaponId)) {
        weaponRegistryLogger.warn(`WeaponRegistry: Default weapon '${weaponId}' not found`);
        return;
      }
      this.defaultWeaponId = weaponId;
    }

    /**
     * Get the default weapon
     * @returns {Weapon}
     */
    getDefault() {
      const weapon = this.weapons.get(this.defaultWeaponId) || this.getAll()[0];
      if (!weapon) {
        weaponRegistryLogger.warn('[WeaponRegistry] getDefault() before any weapon was registered — call loadRegistries() (server) or ConfigRegistry.loadAll() (client) first');
      }
      return weapon || null;
    }

    /**
     * Get weapons filtered by criteria
     * @param {Object} filters - { rarity?, lootSource?, canDrop?, fireMode? }
     * @returns {Weapon[]}
     */
    query(filters = {}) {
      return this.getAll().filter(weapon => {
        if (filters.rarity && weapon.rarity !== filters.rarity) return false;
        if (filters.lootSource && !weapon.lootSources.includes(filters.lootSource)) return false;
        if (filters.canDrop !== undefined && weapon.canDrop !== filters.canDrop) return false;
        if (filters.fireMode && weapon.fireMode !== filters.fireMode) return false;
        return true;
      });
    }

    /**
     * Get weapons by rarity
     * @param {string} rarity - ItemRarity value
     * @returns {Weapon[]}
     */
    getByRarity(rarity) {
      return this.query({ rarity });
    }

    /**
     * Get weapons that can drop from a specific source
     * @param {string} source - e.g., 'ground', 'chest', 'enemy'
     * @returns {Weapon[]}
     */
    getByLootSource(source) {
      return this.query({ lootSource: source, canDrop: true });
    }

    /**
     * Get a random weapon using weighted selection
     * @param {Object} filters - Optional filters to apply first
     * @returns {Weapon|null}
     */
    getRandomWeighted(filters = {}) {
      const candidates = filters ? this.query(filters) : this.getAll();
      if (candidates.length === 0) return null;

      const totalWeight = candidates.reduce((sum, w) => sum + w.dropWeight, 0);
      let random = Math.random() * totalWeight;

      for (const weapon of candidates) {
        random -= weapon.dropWeight;
        if (random <= 0) return weapon;
      }

      return candidates[candidates.length - 1];
    }

    /**
     * Get weapon stats for UI display
     * @param {string} weaponId
     * @returns {Object|null}
     */
    getStats(weaponId) {
      const weapon = this.get(weaponId);
      if (!weapon) return null;

      return {
        id: weapon.id,
        name: weapon.name,
        description: weapon.description,
        rarity: weapon.rarity,
        fireRate: weapon.fireRate,
        damage: weapon.damage,
        magCapacity: weapon.magCapacity,
        reloadTime: weapon.reloadTime,
        projectileSpeed: weapon.projectileSpeed
      };
    }

    /**
     * Get all weapon stats for UI
     * @returns {Object[]}
     */
    getAllStats() {
      return this.getAllIds().map(id => this.getStats(id));
    }

    /**
     * Serialize registry for network transmission
     * @returns {Object}
     */
    serialize() {
      const data = {};
      for (const [id, weapon] of this.weapons) {
        data[id] = weapon.serialize();
      }
      return data;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // SINGLETON INSTANCE
  // ─────────────────────────────────────────────────────────────
  const weaponRegistry = new WeaponRegistry();

  // Population is the environment's job, not this module's:
  //   server: src/server/runtime/registries.js → weaponRegistry.registerBulk(config.weapons)
  //   client: public/core/ConfigRegistry.js  → loadWeapons(): GET /config/weapons.json, then registerBulk
  // A registry that is queried before being populated logs a warning from getDefault().

  // ─────────────────────────────────────────────────────────────
  // LEGACY COMPATIBILITY FUNCTIONS
  // ─────────────────────────────────────────────────────────────

  /**
   * Get weapon by ID (legacy - use weaponRegistry.get() instead)
   * @param {string} weaponId - Weapon identifier
   * @returns {Weapon|null}
   */
  function getWeapon(weaponId) {
    return weaponRegistry.get(weaponId);
  }

  /**
   * Get all weapon IDs (legacy - use weaponRegistry.getAllIds() instead)
   * @returns {string[]}
   */
  function getAllWeaponIds() {
    return weaponRegistry.getAllIds();
  }

  /**
   * Get default weapon (legacy - use weaponRegistry.getDefault() instead)
   * @returns {Weapon}
   */
  function getDefaultWeapon() {
    return weaponRegistry.getDefault();
  }

export {
  // New API (recommended)
  WeaponRegistry,
  weaponRegistry,
  // Legacy API (backward compatible - deprecated, use weaponRegistry instead)
  getWeapon,
  getAllWeaponIds,
  getDefaultWeapon
};
